import { useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Menu } from 'Component/Menu/Menu';
import { AccountMenu } from 'Component/AccountMenu/AccountMenu';
import { MenuLink } from 'Component/MenuLink/MenuLink';
import { hasSubscription, hasZppCourseAttendances, isAuthed, isGuest } from 'redux/query';
import { CooperationLogo } from 'Common/CooperationLogo/CooperationLogo';
import { TEXT } from 'locales/translations';
import { compareRouteWithPaths } from 'utils';
import { linkHandler } from 'utils/linkHandler';
import { useRouteData } from 'Application/RouteDataProvider/RouteDataProvider';

export const Navigation = () => {
  const { currentRoute } = useRouteData();

  const location = useLocation();
  const isSubscribed = useSelector(hasSubscription);
  const hasAttendances = useSelector(hasZppCourseAttendances);
  const showSubscriptionLink = !isSubscribed && !hasAttendances;
  const authed = useSelector(isAuthed);
  const guest = useSelector(isGuest);

  const [isOpen, setIsOpen] = useState(false);

  const compareRouteWithPath = (route) => compareRouteWithPaths(location.pathname, route);

  const isMenuHidden = useMemo(
    () => ['/legal/app/*', '/app-resources/*', '/insurance-cooperation-authentication'].some(compareRouteWithPath),
    [location]
  );

  const menuLinkProps = {
    tag: Link,
    location
  };

  // We currently want the underline in the menu for /login AND /signup to be under "Anmelden". This
  // workaround helps. If you find a better solution for this feel free to change it.
  const menuAuthenticationLinkProps = {
    tag: Link,
    location: location.pathname === '/login' ? { pathname: '/signup' } : location,
    to: linkHandler.get('signup')
  };

  const handleOnCloseMenu = () => {
    setIsOpen(false);
  };

  return isMenuHidden ? null : (
    <Menu
      tinted={currentRoute?.tinted}
      className={[
        currentRoute?.tinted && 'background-white',
        currentRoute?.cooperation && `background-${currentRoute?.cooperationColor || 'blue'}`
      ]
        .filter((c) => !!c)
        .join(' ')}
      cooperationLogo={currentRoute?.cooperation && <CooperationLogo />}
      open={isOpen}
      onToggleNavbar={setIsOpen}
    >
      {!currentRoute?.hideMenuLinks && (
        <>
          <MenuLink {...menuLinkProps} to={linkHandler.get('home')}>
            {TEXT.header.menu.home}
          </MenuLink>
          <MenuLink {...menuLinkProps} to={linkHandler.get('krankenkasse')}>
            {TEXT.header.menu.healthInsurance}
          </MenuLink>
          <MenuLink {...menuLinkProps} to={linkHandler.get('company')}>
            {TEXT.header.menu.company}
          </MenuLink>
          <MenuLink
            {...menuLinkProps}
            to={linkHandler.get('library')}
            activeAvailableRoutes={[linkHandler.get('category'), linkHandler.get('play')]}
          >
            {TEXT.header.menu.library}
          </MenuLink>
          {showSubscriptionLink && (
            <MenuLink {...menuLinkProps} to={linkHandler.get('plans')} activeAvailableRoutes={[linkHandler.get('checkout')]}>
              {TEXT.header.menu.plans}
            </MenuLink>
          )}
          {authed && !guest ? (
            <AccountMenu closeMenu={handleOnCloseMenu} />
          ) : (
            <MenuLink {...menuAuthenticationLinkProps}>{TEXT.header.menu.login}</MenuLink>
          )}
        </>
      )}
    </Menu>
  );
};
